/**
 * Operation Context Factories
 *
 * Create context objects for CellOperations, EditorOperations,
 * ViewOperations, and DialogOperations.
 */

import { appState } from '../state/AppState';
import { session } from './puzzleSession';
import { updateMainMenu, updateClueBar, updateHistoryBar } from './menuUpdates';
import type { CellOperationContext } from '../operations/CellOperations';
import type { EditorOperationContext } from '../operations/EditorOperations';
import type { ViewOperationContext } from '../operations/ViewOperations';
import type { DialogOperationContext } from '../operations/DialogOperations';

/**
 * Create cell operation context
 */
export function getCellOperationContext(): CellOperationContext | null {
    if (!session.currentPuzzle || !session.historyManager) return null;
    return {
        puzzle: session.currentPuzzle,
        history: session.historyManager,
        appState,
        onMenuUpdate: updateMainMenu,
        onHistoryUpdate: () => {
            updateHistoryBar();
            updateMainMenu();
        },
    };
}

/**
 * Create editor operation context
 */
export function getEditorOperationContext(): EditorOperationContext | null {
    if (!session.currentPuzzle) return null;
    return {
        puzzle: session.currentPuzzle,
        appState,
        onMenuUpdate: updateMainMenu,
        onSelectionChange: () => {
            // Re-select current cell to update selection with new direction
            if (session.cellEvents) {
                session.cellEvents.navigateTo(appState.currentCellId);
            }
            updateClueBar();
        },
    };
}

/**
 * Create view operation context
 */
export function getViewOperationContext(): ViewOperationContext | null {
    if (!session.currentPuzzle) return null;
    return {
        puzzle: session.currentPuzzle,
        appState,
        onMenuUpdate: updateMainMenu,
    };
}

/**
 * Create dialog operation context
 */
export function getDialogOperationContext(): DialogOperationContext | null {
    if (!session.currentPuzzle || !session.historyManager) return null;
    return {
        puzzle: session.currentPuzzle,
        history: session.historyManager,
        appState,
        onMenuUpdate: updateMainMenu,
        onHistoryUpdate: () => {
            updateHistoryBar();
            updateMainMenu();
        },
    };
}
